import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAddItemToCartMutation } from "../../../cart/api/cartApi";
import type { TransformedProduct } from "../../../product/types/productApiTypes";
import type { AddCartItemParams } from "../../../cart/types/cartApiTypes";

// --- Cart Message Type ---
interface CartMessage {
  type: "success" | "error";
  text: string;
}

// --- Component Props ---
interface ProductCardActionsProps {
  product: TransformedProduct;
}

// --- Component Implementation ---
const ProductCardActions: React.FC<ProductCardActionsProps> = ({ product }) => {
  const { t } = useTranslation();
  const [cartMessage, setCartMessage] = useState<CartMessage | null>(null);
  const [addItemToCart, { isLoading: addingToCart }] =
    useAddItemToCartMutation();

  // --- Determine Product ID and Stock Status to Use ---
  const isVariable = product.type === "variable";
  // Use default variation ID if available for variable products, otherwise use parent ID
  const productIdToAdd =
    isVariable && product.defaultVariationData?.id
      ? product.defaultVariationData.id
      : product.id;

  // Use product's pre-calculated stock status (which reflects default variation if applicable)
  const stockStatusToUse = product.stockStatus;
  const isOutOfStock = stockStatusToUse === "outofstock";

  // --- Event Handler for Add to Cart ---
  const handleAddToCart = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault(); // Prevent default form submission if it were a form
    e.stopPropagation(); // Prevent triggering the parent link if clicked fast
    setCartMessage(null);

    if (isOutOfStock || addingToCart) {
      return;
    }

    const itemToAdd: AddCartItemParams = {
      id: productIdToAdd,
      quantity: 1,
    };

    try {
      await addItemToCart(itemToAdd).unwrap();
      setCartMessage({
        type: "success",
        text: t("addToCart.successMessage"),
      });
      // Add a class briefly for visual feedback?
      // e.currentTarget.classList.add('added');
      // setTimeout(() => e.currentTarget.classList.remove('added'), 1000);
    } catch (err: any) {
      console.error("Failed to add item to cart from card:", err);
      let errorMessage = t("addToCart.errorGeneral"); // Default general error

      // Check for specific error code
      if (
        err?.data?.code === "woocommerce_rest_product_partially_out_of_stock"
      ) {
        // Use the specific message translation
        errorMessage = t("addToCart.errorPartiallyOutOfStock");
      }
      setCartMessage({
        type: "error",
        text: errorMessage,
      });
    }
  };

  // --- Effect to clear message after a delay ---
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (cartMessage) {
      timer = setTimeout(() => setCartMessage(null), 2500);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [cartMessage]);

  // --- Determine Button Text ---
  let buttonText = t("product.addToCart");
  if (addingToCart) {
    buttonText = t("addToCart.adding");
  } else if (isOutOfStock) {
    buttonText = t("product.outOfStock");
  }
  // Note: Showing "Added!" directly on the button might be less ideal than the message

  // --- Render ---
  return (
    <div className="product-card-actions">
      {/* View Details Link */}
      <a href={product.permalink} className="view-product-btn">
        {t("common.viewProduct")}
      </a>

      {/* Add to Cart Button & Message Wrapper */}
      <div className="product-card-add-to-cart-wrapper">
        <button
          className={`add-to-cart-btn ${isOutOfStock ? "disabled" : ""} ${
            addingToCart ? "loading" : ""
          }`}
          onClick={handleAddToCart}
          disabled={isOutOfStock || addingToCart}
          aria-label={t("product.addToCart")}
        >
          {buttonText}
        </button>
        {/* Render message - position with CSS */}
        {cartMessage && (
          <div className={`cart-message-inline ${cartMessage.type}`}>
            {cartMessage.text}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCardActions;
